<script setup>
import {
  computed,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
import {
  COUPON_TYPE_OPTIONS,
} from '@/config/constant';
import addComma from '@/utils/addComma';
import ImageComponent from '@/components/common/ImageComponent.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  imageUrl: {
    type: String,
    default: '',
  },
  regularPrice: {
    type: String,
    default: '',
  },
  discountPrice: {
    type: String,
    default: '',
  },
  discountRate: {
    type: String,
    default: '',
  },
  discountFixed: {
    type: String,
    default: '',
  },
  additionalContent: {
    type: String,
    default: '',
  },
  buttons: {
    type: Array,
    default: () => ([]),
  },
  coupon: {
    type: Object,
    default: () => ({}),
  },
  position: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();

const {
  position,
  index,
  title,
  regularPrice,
  discountPrice,
  discountRate,
  discountFixed,
  additionalContent,
  buttons,
  coupon,
} = toRefs(props);

const displayTitle = computed(() => title.value || t('kakaoBasic.preview.productName'));

const displayDiscountPrice = computed(() => {
  if (discountPrice.value) {
    return `${addComma(discountPrice.value)}`;
  }

  return t('kakaoBasic.preview.discountPrice');
});

const discountPriceSuffix = computed(() => {
  if (discountPrice.value) {
    return t('kakaoBasic.preview.KRW');
  }

  return '';
});

const displayRegularPrice = computed(() => {
  if (regularPrice.value) {
    if (!discountPrice.value) {
      return `${addComma(regularPrice.value)}`;
    }

    return addComma(regularPrice.value);
  }

  return t('kakaoBasic.preview.regularPrice');
});

const regularPriceSuffix = computed(() => {
  if (regularPrice.value) {
    if (!discountPrice.value) {
      return t('kakaoBasic.preview.KRW');
    }

    return '';
  }

  return '';
});

const displayDiscountRate = computed(() => {
  if (discountRate.value) {
    return discountRate.value;
  }

  if (discountFixed.value) {
    return '';
  }

  return t('kakaoBasic.preview.discountRate');
});

const displayDiscountFixed = computed(() => {
  if (discountFixed.value) {
    return `${addComma(discountFixed.value)}`;
  }

  return t('kakaoBasic.preview.discountFixed');
});

const discountFixedSuffix = computed(() => {
  if (discountFixed.value) {
    return t('kakaoBasic.preview.KRW');
  }

  return '';
});

const displayAdditionalContent = computed(() => additionalContent.value || '');

const itemHoverClass = computed(() => {
  if (position.value === `carousel-commerce-${index.value}`) {
    return 'editAreaPositionHover';
  }

  return '';
});

const imageHoverClass = computed(() => {
  if (position.value === `carousel-commerce-${index.value}-image`) {
    return 'editAreaPositionHover';
  }

  return '';
});

const titleHoverClass = computed(() => {
  if (position.value === `carousel-commerce-${index.value}-title`) {
    return 'editAreaPositionHover';
  }

  return '';
});

const buttonsList = computed(() => {
  if (buttons.value?.length) {
    return buttons.value;
  }

  return [{ name: t('placeholder.button') }];
});

const buttonsHoverClass = computed(() => {
  if (position.value === `carousel-commerce-${index.value}-buttons` && buttonsList.value?.length) {
    return 'editAreaPositionHover';
  }

  return '';
});

const discountPriceClass = computed(() => {
  const classList = [];
  const hover = position.value === `carousel-commerce-${index.value}-discountPrice`;

  if (regularPrice.value && !discountPrice.value) {
    classList.push('d-none');
  } else {
    classList.push('price__sale');
  }

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!regularPrice.value) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const regularPriceClass = computed(() => {
  const classList = [];
  const hover = position.value === `carousel-commerce-${index.value}-regularPrice`;

  if (regularPrice.value && !discountPrice.value) {
    classList.push('price__sale');
  } else {
    classList.push('price__basic');
  }

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!regularPrice.value) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const discountRateClass = computed(() => {
  const classList = [];
  const hover = position.value === `carousel-commerce-${index.value}-discountRate`;

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!displayDiscountRate.value) {
    classList.push('d-none');
  }

  if (regularPrice.value && !discountPrice.value && !discountRate.value) {
    classList.push('d-none');
  }

  if (!discountRate.value) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const discountFixedClass = computed(() => {
  const classList = [];
  const hover = position.value === `carousel-commerce-${index.value}-discountFixed`;

  if (displayDiscountRate.value) {
    classList.push('d-none');
  }

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  return classList.join(' ');
});

const additionalContentClass = computed(() => {
  if (!displayAdditionalContent.value) {
    return 'd-none';
  }

  const hover = position.value === `carousel-commerce-${index.value}-additionalContent`;

  return hover ? 'editAreaPositionHover' : '';
});

const couponClass = computed(() => {
  const list = [];
  const hover = position.value === `carousel-commerce-${index.value}-coupon`;

  if (hover) {
    list.push('editAreaPositionHover');
  }

  if (!coupon.value?.enabled) {
    list.push('d-none');
  }

  return list.join(' ');
});

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const couponTitle = computed(() => {
  const name = coupon.value?.name;
  const type = coupon.value?.type;
  const str = t(couponType[type] ?? 'empty');
  const value = type === 'DELIVERY_DISCOUNT' ? str : `${name}${str.slice(2)}`;

  return value;
});

const couponDescription = computed(() => coupon.value?.description || '');

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <li
    class="swiper-slide"
    :class="itemHoverClass"
  >
    <div class="preview-message__container">
      <div class="preview-message__content">
        <div
          class="preview-message__inner-img"
          :class="imageHoverClass"
        >
          <ImageComponent
            :src="imageUrl"
            :alt="`carousel-image${index}`"
          />
        </div>
        <div class="preview-message__inner">
          <div>
            <div
              class="carousel-feed__desc"
              :class="titleHoverClass"
            >
              {{ displayTitle }}
            </div>
            <div class="carousel-feed__price">
              <div :class="discountPriceClass">
                <span>{{ displayDiscountPrice }}</span>{{ discountPriceSuffix }}
              </div>
              <div :class="regularPriceClass">
                <span>{{ displayRegularPrice }}</span>{{ regularPriceSuffix }}
              </div>
              <div
                class="price__percent"
                :class="discountRateClass"
              >
                <span>{{ displayDiscountRate }}</span><v-icon
                  class="custom__icon"
                  icon="mdi-percent-outline"
                  size="small"
                />
              </div>
              <div
                class="price__percent"
                :class="discountFixedClass"
              >
                <span>{{ displayDiscountFixed }}</span>{{ discountFixedSuffix }}<v-icon
                  class="custom__icon"
                  icon="mdi-arrow-down"
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          class="carousel-feed__desc"
          :class="additionalContentClass"
        >
          <p>
            {{ displayAdditionalContent }}
          </p>
        </div>
        <div
          class="preview-message__button preview-message__button--horizontal"
          :class="buttonsHoverClass"
        >
          <button
            v-for="(elm, i) in buttonsList"
            :key="`preview-carouselCommerce-${index}-button-${i}`"
            :class="{ 'preview-message__button--yellow': elm.type === 'BF' }"
            type="button"
          >
            {{ elm.name }}
          </button>
        </div>
        <div class="preview-message__coupon">
          <button
            type="button"
            :class="couponClass"
          >
            <div class="coupon__text">
              <h5 class="coupon__title">{{ couponTitle }}</h5>
              <p class="coupon__desc">{{ couponDescription }}</p>
            </div>
            <div class="coupon__icon">
              <i></i>
              <span>COUPON</span>
            </div>
          </button>
        </div>

        <PreviewFooter />
      </div>
    </div>
  </li>
</template>
<style>
</style>
